<template>
  <div class="soft-skills-growth-card">
    <div class="soft-skills-growth-card__score">
      {{ formattedScore }}
    </div>

    <div class="soft-skills-growth-card__change">
      <span
          :class="{
            'positive-change': safeChange > 0,
            'negative-change': safeChange < 0,
            'neutral-change': safeChange === 0
          }"
      >
        {{ changeText }}
      </span>

      <span>{{ changeLabel }}</span>
    </div>

    <div class="soft-skills-growth-card__indexes">
      <div
          v-for="item in normalizedIndexes"
          :key="item.label"
          class="soft-skills-growth-card__index-row"
      >
        <span>{{ item.label }}</span>

        <span
            class="soft-skills-growth-card__index-value"
            :class="{
            'soft-skills-growth-card__index-value--underlined': item.value !== 0
          }"
        >
          {{ item.value }} чел.
        </span>
      </div>
    </div>

    <div
        ref="chartWrapper"
        class="soft-skills-growth-card__chart"
    >
      <canvas ref="chartCanvas"></canvas>

      <div
          v-if="!hasChartData"
          class="soft-skills-growth-card__chart-empty"
      >
        Не хватает данных за текущий период
      </div>
    </div>
  </div>
</template>

<script>
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler,
  Tooltip,
} from 'chart.js'
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue'

Chart.register(
    LineController,
    LineElement,
    PointElement,
    LinearScale,
    CategoryScale,
    Filler,
    Tooltip,
)

export default {
  name: 'SoftSkillsGrowthCard',

  props: {
    score: {
      type: Number,
      default: 0,
    },
    change: {
      type: Number,
      default: 0,
    },
    changeLabel: {
      type: String,
      default: '',
    },
    indexes: {
      type: Array,
      default: () => [],
    },
    chartLabels: {
      type: Array,
      default: () => [],
    },
    chartValues: {
      type: Array,
      default: () => [],
    },
  },

  setup(props) {
    const chartCanvas = ref(null)
    const chartWrapper = ref(null)

    let chartInstance = null
    let resizeObserver = null
    let resizeFrame = null

    const Y_STEP = 25
    const MIN_SIDE_RANGE = 50

    const safeScore = computed(() => {
      const value = Number(props.score)
      return Number.isFinite(value) ? value : 0
    })

    const safeChange = computed(() => {
      const value = Number(props.change)
      return Number.isFinite(value) ? value : 0
    })

    const formattedScore = computed(() => {
      return Number.isInteger(safeScore.value)
          ? safeScore.value
          : safeScore.value.toFixed(1)
    })

    const changeText = computed(() => {
      if (safeChange.value > 0) {
        return `+${safeChange.value}%`
      }

      if (safeChange.value < 0) {
        return `${safeChange.value}%`
      }

      return '0%'
    })

    const normalizedIndexes = computed(() => {
      return props.indexes.map((item) => {
        const value = Number(item?.value)

        return {
          ...item,
          value: Number.isFinite(value) ? value : 0,
        }
      })
    })

    const normalizedChartLabels = computed(() => {
      if (Array.isArray(props.chartLabels) && props.chartLabels.length) {
        return props.chartLabels
      }

      return ['']
    })

    const normalizedChartValues = computed(() => {
      return normalizedChartLabels.value.map((_, index) => {
        const value = props.chartValues[index]
        const numericValue = Number(value)

        return Number.isFinite(numericValue) ? numericValue : null
      })
    })

    const validChartValues = computed(() => {
      return normalizedChartValues.value.filter((value) => value !== null)
    })

    const hasChartData = computed(() => {
      return validChartValues.value.length >= 2
    })

    const chartDatasetValues = computed(() => {
      if (!hasChartData.value) {
        return normalizedChartLabels.value.map(() => null)
      }

      return normalizedChartValues.value
    })

    const yAxisLimit = computed(() => {
      if (!validChartValues.value.length) {
        return MIN_SIDE_RANGE
      }

      const maxAbsValue = Math.max(
          ...validChartValues.value.map((value) => Math.abs(value))
      )

      let limit = Math.max(
          MIN_SIDE_RANGE,
          Math.ceil(maxAbsValue / 50) * 50
      )

      if (maxAbsValue === limit) {
        limit += 50
      }

      return limit
    })

    const getChartGradient = (ctx, chartArea) => {
      const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
      gradient.addColorStop(1, 'rgba(33, 172, 12, 0)');

      return gradient
    }

    const destroyChart = () => {
      if (chartInstance) {
        chartInstance.destroy()
        chartInstance = null
      }
    }

    const forceResizeChart = () => {
      if (!chartInstance || !chartWrapper.value) {
        return
      }

      const width = chartWrapper.value.clientWidth
      const height = chartWrapper.value.clientHeight

      if (!width || !height) {
        return
      }

      chartInstance.resize(width, height)
      chartInstance.update('none')
    }

    const scheduleResize = () => {
      if (resizeFrame) {
        cancelAnimationFrame(resizeFrame)
      }

      resizeFrame = requestAnimationFrame(() => {
        forceResizeChart()
      })
    }

    const createChart = () => {
      if (!chartCanvas.value) {
        return
      }

      const ctx = chartCanvas.value.getContext('2d')

      chartInstance = new Chart(ctx, {
        type: 'line',
        data: {
          labels: normalizedChartLabels.value,
          datasets: [
            {
              data: chartDatasetValues.value,
              borderColor: hasChartData.value ? '#21AC0C' : 'rgba(0,0,0,0)',
              backgroundColor: (context) => {
                if (!hasChartData.value) {
                  return 'rgba(0,0,0,0)'
                }

                const chart = context.chart
                const { chartArea } = chart

                if (!chartArea) {
                  return
                }

                return getChartGradient(chart.ctx, chartArea)
              },
              borderWidth: hasChartData.value ? 1 : 0,
              pointRadius: hasChartData.value ? 3 : 0,
              pointHoverRadius: hasChartData.value ? 4 : 0,
              pointBackgroundColor: hasChartData.value
                  ? '#21AC0C'
                  : 'rgba(0,0,0,0)',
              pointBorderColor: hasChartData.value
                  ? '#21AC0C'
                  : 'rgba(0,0,0,0)',
              fill: hasChartData.value,
              tension: 0,
              spanGaps: false,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          resizeDelay: 0,

          layout: {
            padding: {
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
            },
          },

          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              enabled: hasChartData.value,
              displayColors: false,
              callbacks: {
                label: (context) => {
                  const value = context.parsed.y

                  if (value > 0) {
                    return `+${value}%`
                  }

                  return `${value}%`
                },
              },
            },
            datalabels: {
              display: false,
            },
          },

          scales: {
            x: {
              offset: false,
              grid: {
                color: '#EEF0F3',
                drawTicks: false,
                drawBorder: false,
              },
              border: {
                display: false,
              },
              ticks: {
                color: 'rgba(0, 0, 0, 0.45)',
                padding: 8,
                font: {
                  size: 12,
                },
              },
            },

            y: {
              min: -yAxisLimit.value,
              max: yAxisLimit.value,
              bounds: 'ticks',
              ticks: {
                stepSize: Y_STEP,
                padding: 6,
                color: 'rgba(0, 0, 0, 0.45)',
                font: {
                  size: 12,
                },
                callback: (value) => {
                  if (value > 0) {
                    return `+${value}`
                  }

                  return value
                },
              },
              grid: {
                drawTicks: false,
                drawBorder: false,
                color: (context) => {
                  return context.tick.value === 0 ? '#EEF0F3' : 'transparent'
                },
              },
              border: {
                display: false,
              },
            },
          },
        },
      })
    }

    const renderChart = async () => {
      await nextTick()

      destroyChart()
      createChart()

      await nextTick()
      scheduleResize()
    }

    const handleWindowResize = () => {
      scheduleResize()
    }

    onMounted(async () => {
      await renderChart()

      window.addEventListener('resize', handleWindowResize)

      if (chartWrapper.value) {
        resizeObserver = new ResizeObserver(() => {
          scheduleResize()
        })

        resizeObserver.observe(chartWrapper.value)
      }

      setTimeout(() => {
        scheduleResize()
      }, 50)
    })

    onBeforeUnmount(() => {
      window.removeEventListener('resize', handleWindowResize)

      if (resizeObserver) {
        resizeObserver.disconnect()
        resizeObserver = null
      }

      if (resizeFrame) {
        cancelAnimationFrame(resizeFrame)
        resizeFrame = null
      }

      destroyChart()
    })

    watch(
        () => [props.chartLabels, props.chartValues],
        async () => {
          await renderChart()
        },
        {
          deep: true,
        }
    )

    return {
      chartCanvas,
      chartWrapper,
      formattedScore,
      safeChange,
      changeText,
      normalizedIndexes,
      hasChartData,
    }
  },
}
</script>

<style scoped lang="scss">
.soft-skills-growth-card {
  width: 100%;
  display: flex;
  flex-direction: column;
  text-align: start;
}

.soft-skills-growth-card__score {
  margin-bottom: 4px;
  color: rgba(0, 0, 0, 0.88);

  font-weight: 600;
  font-size: 24px;
  line-height: 120%;
}

.soft-skills-growth-card__change {
  display: flex;
  gap: 4px;
  margin-bottom: 16px;
  color: rgba(0, 0, 0, 0.65);
  font-size: 14px;
  line-height: 120%;
}

.soft-skills-growth-card__change span:first-child {
  font-weight: 600;
}

.soft-skills-growth-card__indexes {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 24px;
}

.soft-skills-growth-card__index-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.65);
}

.soft-skills-growth-card__index-value {
  color: rgba(0, 0, 0, 0.88);
  white-space: nowrap;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
}

.soft-skills-growth-card__index-value--underlined {
  text-decoration: underline;
  cursor: pointer;
}

.soft-skills-growth-card__chart {
  position: relative;
  width: 100%;
  height: 122px;
  margin-top: auto;
  min-width: 0;
}

.soft-skills-growth-card__chart canvas {
  display: block;
  width: 100% !important;
  height: 100% !important;
}

.soft-skills-growth-card__chart-empty {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;

  padding: 6px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  background: #fff;
  font-weight: 400;
  font-size: 12px;
  line-height: 110%;
  text-align: center;
  white-space: nowrap;
}

.positive-change {
  color: var(--main-green);
}

.negative-change {
  color: #F55565;
}

.neutral-change {
  color: rgba(0, 0, 0, 0.45);
}
</style>